import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { DateTime } from "luxon";
import DashboardWrapper from "../components/layout/DashboardWrapper";
import { adminService, PaginatedUsersResult } from "../services/adminService";
import { UserData } from "../types/user";
import { MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { doc, DocumentSnapshot, getDoc, serverTimestamp, setDoc, collection, query, orderBy, getDocs, deleteDoc } from "firebase/firestore";
import UpdateSubscriptionDialog from "../components/UpdateSubscriptionDialog";
import CreateUserDialog from "../components/CreateUserDialog";
import UserDetailSidebar, { WeeklyUserReport } from "../components/UserDetailSidebar";
import { useAuth } from "../contexts/AuthContext";
import { db, defaultDb, functions } from "../firebase/config";
import { getFunctions, httpsCallable } from "firebase/functions";

const generateWeeklyReports = httpsCallable(functions, "weeklyReportGenerator");

const UsersPage: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const {currentUser} = useAuth();

    const [users, setUsers] = useState<(UserData & { uid: string })[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [lastDoc, setLastDoc] = useState<DocumentSnapshot | null>(null);
    const [hasMore, setHasMore] = useState(false);

    const [searchTerm, setSearchTerm] = useState("");
    const [searchResults, setSearchResults] = useState<(UserData & { uid: string })[] | null>(null);
    const [searching, setSearching] = useState(false);

    const [userCount, setUserCount] = useState<number | null>(null);
    const [coachCount, setCoachCount] = useState<number | null>(null);

    const [showCreateUser, setShowCreateUser] = useState(false);
    const [createUserEmail, setCreateUserEmail] = useState<string | undefined>(undefined);

    const [selectedUser, setSelectedUser] = useState<(UserData & { uid: string }) | null>(null);
    const [userReports, setUserReports] = useState<WeeklyUserReport[]>([]);
    const [loadingReports, setLoadingReports] = useState(false);
    const [generatingReport, setGeneratingReport] = useState(false);
    const [deletingReport, setDeletingReport] = useState(false);
    const [selectedReport, setSelectedReport] = useState<WeeklyUserReport | null>(null);
    const [subscriptionDialog, setSubscriptionDialog] = useState<{
        isOpen: boolean;
        targetUser: {
            id: string;
            name: string;
            surname: string;
            email: string;
        } | null;
        currentSubscription: string;
    }>({
        isOpen: false,
        targetUser: null,
        currentSubscription: 'default'
    });
    // Load initial users
    const loadUsers = useCallback(async () => {
        setLoading(true);
        try {
            const result: PaginatedUsersResult = await adminService.getUsers(20);
            setUsers(result.users);
            setLastDoc(result.lastDoc);
            setHasMore(result.hasMore);
        } catch (error) {
            console.error("Error loading users:", error);
        } finally {
            setLoading(false);
        }
    }, []);

    // Load more users
    const loadMoreUsers = async () => {
        if (!hasMore || loadingMore) return;

        setLoadingMore(true);
        try {
            const result = await adminService.getUsers(20, lastDoc);
            setUsers((prev) => [...prev, ...result.users]);
            setLastDoc(result.lastDoc);
            setHasMore(result.hasMore);
        } catch (error) {
            console.error("Error loading more users:", error);
        } finally {
            setLoadingMore(false);
        }
    };

    // Search users
    const handleSearch = async () => {
        const term = searchTerm.trim();
        if (!term) {
            setSearchResults(null);
            return;
        }

        setSearching(true);
        try {
            // Check if it looks like a UID (long alphanumeric string)
            if (term.length >= 20 && /^[a-zA-Z0-9]+$/.test(term)) {
                const user = await adminService.searchUserByUid(term);
                setSearchResults(user ? [user] : []);
            } else {
                // Search via Cloud Function
                const result = await adminService.searchUsers(term);
                setSearchResults(result.users);
            }
        } catch (error) {
            console.error("Error searching users:", error);
            setSearchResults([]);
        } finally {
            setSearching(false);
        }
    };

    // Clear search
    const clearSearch = () => {
        setSearchTerm("");
        setSearchResults(null);
    };

    useEffect(() => {
        document.title = "Users - Peak^ Admin";
        loadUsers();

        const fetchCounts = async () => {
            try {
                const [userC, coachC] = await Promise.all([
                    adminService.getUserCount(),
                    adminService.getCoachCount(),
                ]);
                setUserCount(userC);
                setCoachCount(coachC);
            } catch (error) {
                console.error("Error fetching counts:", error);
            }
        };
        fetchCounts();
    }, [loadUsers]);

    const openUserByUid = useCallback(async (userUid: string) => {
        try {
            const user = await adminService.searchUserByUid(userUid);
            if (user) {
                setSelectedUser(user);
            }
        } catch (error) {
            console.error("Error loading user from URL:", error);
        }
    }, []);

    // Load user from URL parameter if present
    useEffect(() => {
        const userUid = searchParams.get("user");
        if (userUid) {
            openUserByUid(userUid);
        }
    }, [searchParams, openUserByUid]);

    // Open the create user dialog when sent here from another page (e.g. organisation details)
    useEffect(() => {
        if (searchParams.get("createUser") === "1") {
            setCreateUserEmail(searchParams.get("email") ?? undefined);
            setShowCreateUser(true);
            setSearchParams({}, { replace: true });
        }
    }, [searchParams, setSearchParams]);

    // Debounced search on Enter key
    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter") {
            handleSearch();
        }
    };

    // Fetch reports for the selected user
    const fetchUserReports = useCallback(async (userId: string) => {
        setLoadingReports(true);
        setUserReports([]);
        try {
            const allReports: WeeklyUserReport[] = [];
            const dbEntries: [typeof db, "version2" | "default"][] = [[db, "version2"], [defaultDb, "default"]];
            for (const [firestore, dbName] of dbEntries) {
                const reportsRef = collection(firestore, "weeklyReports", userId, "reports");
                const q = query(reportsRef, orderBy("generatedAt", "desc"));
                const snapshot = await getDocs(q);
                snapshot.docs.forEach((d) => {
                    allReports.push({ ...d.data() as WeeklyUserReport, userId, _docId: d.id, _dbName: dbName });
                });
                if (allReports.length > 0) break;
            }
            setUserReports(allReports);
        } catch (error) {
            console.error("Error fetching user reports:", error);
        } finally {
            setLoadingReports(false);
        }
    }, []);

    // Fetch reports when selected user changes
    useEffect(() => {
        if (selectedUser) {
            fetchUserReports(selectedUser.uid);
        } else {
            setUserReports([]);
        }
    }, [selectedUser, fetchUserReports]);

    // Generate a weekly report for the selected user
    const handleGenerateReport = async (sendEmail: boolean) => {
        if (!selectedUser || !currentUser) return;

        setGeneratingReport(true);
        try {
            const now = DateTime.now();
            const weekId = `${now.weekYear}-W${String(now.weekNumber).padStart(2, "0")}`;

            await generateWeeklyReports({
                userIds: [selectedUser.uid],
                weekId,
                batchNumber: 1,
                totalBatches: 1,
                sendEmail,
            });

            // Refresh the reports list
            await fetchUserReports(selectedUser.uid);
        } catch (error) {
            console.error("Error generating report:", error);
            alert("Failed to generate report. Check console for details.");
        } finally {
            setGeneratingReport(false);
        }
    };

    const formatReportTimestamp = (ts: { seconds: number; nanoseconds: number } | undefined) => {
        if (!ts) return "N/A";
        return DateTime.fromSeconds(ts.seconds).toLocaleString(DateTime.DATETIME_MED);
    };

    const handleDeleteReport = async (report: WeeklyUserReport) => {
        if (!report._docId || !report._dbName || !report.userId) return;
        if (!window.confirm(`Delete report for ${report.period}? This cannot be undone.`)) return;

        setDeletingReport(true);
        try {
            const firestore = report._dbName === "version2" ? db : defaultDb;
            const reportRef = doc(firestore, "weeklyReports", report.userId, "reports", report._docId);
            await deleteDoc(reportRef);

            setUserReports((prev) => prev.filter((r) => r._docId !== report._docId));
            setSelectedReport(null);
        } catch (error) {
            console.error("Error deleting report:", error);
            alert("Failed to delete report. Check console for details.");
        } finally {
            setDeletingReport(false);
        }
    };

    const handleSubscriptionClick = (user: UserData & { uid: string }) => {
        setSubscriptionDialog({
            isOpen: true,
            targetUser: {
                id: user.uid,
                name: user.name || '',
                surname: user.surname ?? '',
                email: user.email || ''
            },
            currentSubscription: (user as any).subscription?.subscriptionType || 'default'
        });
    };

    const handleSubscriptionDialogClose = () => {
        setSubscriptionDialog({
        isOpen: false,
        targetUser: null,
        currentSubscription: 'default'
        });
    };


    const handleUpdateSubscription = async (userId: string, subscriptionType: any) => {
        if (!currentUser) {
        throw new Error('You must be authenticated to update subscriptions');
        }

        try {
        // 1. Write to adminSubscriptions collection
        const adminSubscriptionRef = doc(db, 'adminSubscriptions', userId);
        await setDoc(adminSubscriptionRef, {
            userId,
            subscriptionType:subscriptionType.value,
            priceId:subscriptionType.priceId,
            lastUpdated: serverTimestamp(),
            subscriptionVia:"admin",
            adminUserID: currentUser.uid
        });

        console.log(`[UPDATE_SUBSCRIPTION] Admin subscription updated for user ${userId}`);

        // 2. Trigger subscription check via Firebase Function
        const functions = getFunctions();
        const triggerCheckUserSubscription = httpsCallable(functions, 'triggerCheckUserSubscription');
        
        const result = await triggerCheckUserSubscription({ userId });
        console.log('[UPDATE_SUBSCRIPTION] Subscription check result:', result.data);

        // 3. Fetch only the updated user's active subscription
        const activeSubscriptionRef = doc(db, 'activeSubscriptions', userId);
        const activeSubscriptionDoc = await getDoc(activeSubscriptionRef);

        if (activeSubscriptionDoc.exists()) {
            const data = activeSubscriptionDoc.data();
            
            // Safely convert Firestore timestamp to date
            let lastUpdatedDate: Date;
            try {
            lastUpdatedDate = data.lastUpdated?.toDate ? data.lastUpdated.toDate() : new Date(data.lastUpdated || Date.now());
            } catch (error) {
            console.warn('Error converting lastUpdated date:', error, data.lastUpdated);
            lastUpdatedDate = new Date(); // Fallback to current date
            }

            const updatedSubscription = {
            userId: activeSubscriptionDoc.id,
            subscriptionType: data.subscriptionType || 'default',
            subscriptionVia: data.subscriptionVia || 'default',
            lastUpdated: lastUpdatedDate,
            subscriptionChangeMessage: data.subscriptionChangeMessage || '',
            subscriptionChangeRead: data.subscriptionChangeRead ?? true,
            };

            // 4. Update only this user in the local state
            setUsers(prevUsers =>
                prevUsers.map(user =>
                    user.uid === userId
                        ? { ...user, subscription: updatedSubscription }
                        : user
                )
            );

            // Also update selectedUser if it's the same user
            if (selectedUser?.uid === userId) {
                setSelectedUser(prev => prev ? { ...prev, subscription: updatedSubscription } : null);
            }

            console.log(`[UPDATE_SUBSCRIPTION] Updated user ${userId} in local state`);
        }

        alert(`Successfully updated subscription for user to ${subscriptionType.label}`);
        } catch (error) {
        console.error('[UPDATE_SUBSCRIPTION] Error updating subscription:', error);
        throw error;
        }
    };

    const displayUsers = searchResults !== null ? searchResults : users;

    return (
        <DashboardWrapper>
            <div className="w-full flex h-[calc(100vh-64px)] overflow-hidden">
                {/* Main Content */}
                <div className={`flex-1 flex flex-col gap-6 p-6 overflow-hidden transition-all duration-300 ${selectedUser ? 'pr-0' : ''}`}>
                    <div className="flex items-center justify-between shrink-0">
                        <h1 className="text-2xl font-heading">User Management</h1>
                        <button
                            onClick={() => {
                                setCreateUserEmail(undefined);
                                setShowCreateUser(true);
                            }}
                            className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800"
                        >
                            Create User
                        </button>
                    </div>

                    {/* Stats Row */}
                    <div className="w-full grid grid-cols-3 gap-2 shrink-0">
                        <div className="w-full bg-white rounded-xl flex flex-col p-4 gap-2 border">
                            <div className="text-2xl text-primary font-bold">
                                {userCount?.toLocaleString() ?? "—"}
                            </div>
                            <div className="text-xs text-gray-500 uppercase">Total Users</div>
                        </div>
                        <div className="w-full bg-white rounded-xl flex flex-col p-4 gap-2 border">
                            <div className="text-2xl text-gray-700 font-bold">
                                {userCount && coachCount
                                    ? (userCount - coachCount).toLocaleString()
                                    : "—"}
                            </div>
                            <div className="text-xs text-gray-500 uppercase">Athletes</div>
                        </div>
                        <div className="w-full bg-white rounded-xl flex flex-col p-4 gap-2 border">
                            <div className="text-2xl text-green-700 font-bold">
                                {coachCount?.toLocaleString() ?? "—"}
                            </div>
                            <div className="text-xs text-gray-500 uppercase">Coaches</div>
                        </div>
                    </div>

                    {/* Search Bar */}
                    <div className="w-full flex gap-2 shrink-0">
                        <div className="relative flex-1">
                            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search by username, email, or UID..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                onKeyDown={handleKeyDown}
                                className="w-full pl-10 pr-10 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                            />
                            {searchTerm && (
                                <button
                                    onClick={clearSearch}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                >
                                    <XMarkIcon className="w-5 h-5" />
                                </button>
                            )}
                        </div>
                        <button
                            onClick={handleSearch}
                            disabled={searching || !searchTerm.trim()}
                            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {searching ? "Searching..." : "Search"}
                        </button>
                        {searchResults !== null && (
                            <button
                                onClick={clearSearch}
                                className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                            >
                                Clear
                            </button>
                        )}
                    </div>

                    {/* User List */}
                    <div className="flex-1 min-h-0 bg-white rounded-xl border overflow-hidden flex flex-col">
                        {/* Table Header */}
                        <div className="grid grid-cols-12 gap-4 px-4 py-3 bg-gray-50 border-b text-xs font-medium text-gray-500 uppercase shrink-0">
                            <div className="col-span-1">Avatar</div>
                            <div className="col-span-2">Name</div>
                            <div className="col-span-2">Username</div>
                            <div className="col-span-3">Email</div>
                            <div className="col-span-2">Role</div>
                            <div className="col-span-2">Subscription</div>
                        </div>

                        {/* Table Body */}
                        <div className="flex-1 overflow-y-auto min-h-0">
                            {loading ? (
                                <div className="flex items-center justify-center h-32 text-gray-400">
                                    Loading users...
                                </div>
                            ) : displayUsers.length === 0 ? (
                                <div className="flex items-center justify-center h-32 text-gray-400">
                                    {searchResults !== null
                                        ? "No users found matching your search"
                                        : "No users found"}
                                </div>
                            ) : (
                                <>
                                    {displayUsers.map((user) => (
                                        <div
                                            key={user.uid}
                                            onClick={() => setSelectedUser(user)}
                                            className={`grid grid-cols-12 gap-4 px-4 py-3 border-b hover:bg-gray-50 items-center cursor-pointer transition-colors ${
                                                selectedUser?.uid === user.uid ? 'bg-primary/5 border-l-2 border-l-primary' : ''
                                            }`}
                                        >
                                            <div className="col-span-1">
                                                {user.imageUrl ? (
                                                    <img
                                                        src={user.imageUrl}
                                                        alt={user.name || "User"}
                                                        className="w-8 h-8 rounded-full object-cover"
                                                    />
                                                ) : (
                                                    <div className="w-8 h-8 rounded-full bg-primary font-grotesk text-white flex items-center justify-center text-xs font-medium">
                                                        {user.name?.charAt(0).toUpperCase() || "?"}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="col-span-2 truncate">
                                                <div className="font-medium text-sm capitalize">
                                                    {user.name} {user.surname}
                                                </div>
                                            </div>
                                            <div className="col-span-2 text-sm text-gray-600 truncate lowercase">
                                                {user.username ? (user.username.startsWith("@") ? user.username : `@${user.username}`) : ""}
                                            </div>
                                            <div className="col-span-3 text-sm text-gray-600 truncate lowercase">
                                                {user.email || "—"}
                                            </div>
                                            <div className="col-span-2">
                                                <span
                                                    className={`inline-flex px-2 py-1 text-xs rounded-full capitalize ${
                                                        user.role === "coach" || user.role === "Coach"
                                                            ? "bg-green-100 text-green-700"
                                                            : "bg-gray-100 text-gray-700"
                                                    }`}
                                                >
                                                    {user.role || "user"}
                                                </span>
                                            </div>
                                            <div className="col-span-2 flex items-center gap-1 capitalize">
                                                <span
                                                    className={`inline-flex px-2 py-1 text-xs rounded-full capitalize ${
                                                        user.subscription?.subscriptionType === "default"
                                                            ? "bg-gray-100 text-gray-700"
                                                            : ""
                                                    } ${
                                                        user.subscription?.subscriptionType === "premium"
                                                            ? "bg-blue-100 text-blue-700"
                                                            : ""
                                                    }${
                                                        user.subscription?.subscriptionType === "coach"
                                                            ? "bg-green-100 text-green-700"
                                                            : ""
                                                    }`}
                                                >
                                                    {user.subscription?.subscriptionType}
                                                </span>
                                                {user.subscription?.subscriptionVia !== 'default' && (
                                                    <span
                                                        className={`inline-flex px-2 py-1 text-xs rounded-full capitalize ${
                                                            user.subscription?.subscriptionVia === "admin"
                                                                ? "bg-gray-100 text-gray-700"
                                                                : ""
                                                        } ${
                                                            user.subscription?.subscriptionVia === "stripe"
                                                                ? "bg-blue-100 text-blue-700"
                                                                : ""
                                                        }${
                                                            user.subscription?.subscriptionVia === "coach"
                                                                ? "bg-green-100 text-green-700"
                                                                : ""
                                                        }`}
                                                    > {user.subscription?.subscriptionVia}</span>
                                                )}
                                            </div>
                                        </div>
                                    ))}

                                    {/* Load More Button */}
                                    {searchResults === null && hasMore && (
                                        <div className="p-4 flex justify-center">
                                            <button
                                                onClick={loadMoreUsers}
                                                disabled={loadingMore}
                                                className="px-4 py-2 text-sm text-primary border border-primary rounded-lg hover:bg-primary/5 disabled:opacity-50"
                                            >
                                                {loadingMore ? "Loading..." : "Load More"}
                                            </button>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>

                {/* User Details Sidebar */}
                <UserDetailSidebar
                    user={selectedUser}
                    onClose={() => {
                        setSelectedUser(null);
                        if (searchParams.has("user")) setSearchParams({}, { replace: true });
                    }}
                    onSubscriptionClick={handleSubscriptionClick}
                    onGenerateReport={handleGenerateReport}
                    generatingReport={generatingReport}
                    userReports={userReports}
                    loadingReports={loadingReports}
                    onSelectReport={setSelectedReport}
                    formatReportTimestamp={formatReportTimestamp}
                />
                {/* Update Subscription Dialog */}
                {subscriptionDialog.targetUser && (
                    <UpdateSubscriptionDialog
                    isOpen={subscriptionDialog.isOpen}
                    onClose={handleSubscriptionDialogClose}
                    targetUser={subscriptionDialog.targetUser}
                    currentSubscription={subscriptionDialog.currentSubscription}
                    onUpdate={handleUpdateSubscription}
                    />
                )}

                {/* Report Detail Modal */}
                {selectedReport && (
                    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setSelectedReport(null)}>
                        <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center justify-between p-4 border-b">
                                <div>
                                    <h2 className="text-lg font-semibold">Weekly Report</h2>
                                    <p className="text-sm text-gray-500">{selectedReport.period}</p>
                                </div>
                                <button
                                    onClick={() => setSelectedReport(null)}
                                    className="p-1 hover:bg-gray-100 rounded"
                                >
                                    <XMarkIcon className="w-5 h-5" />
                                </button>
                            </div>
                            <div className="flex-1 overflow-y-auto p-4">
                                <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg text-sm whitespace-pre-wrap">
                                    {JSON.stringify(selectedReport, null, 2)}
                                </pre>
                            </div>
                            <div className="flex justify-end p-4 border-t">
                                <button
                                    onClick={() => handleDeleteReport(selectedReport)}
                                    disabled={deletingReport}
                                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {deletingReport ? "Deleting..." : "Delete Report"}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            {showCreateUser && (
                <CreateUserDialog
                    initialEmail={createUserEmail}
                    onClose={() => {
                        setShowCreateUser(false);
                        setCreateUserEmail(undefined);
                    }}
                    onViewUser={(uid) => {
                        setShowCreateUser(false);
                        setSearchParams({ user: uid });
                        openUserByUid(uid);
                    }}
                    onCreated={() => {
                        loadUsers();
                        adminService.getUserCount().then(setUserCount).catch(console.error);
                    }}
                />
            )}
        </DashboardWrapper>
    );
};

export default UsersPage;
